import React, { useMemo } from 'react';
import type {
  CampaignFileStatus,
  EmailExclusionJob,
  ExclusionFileStatus,
} from '../types';
import { Tree, Tag, Tooltip, Modal, Alert } from 'antd';
import * as _ from 'lodash';
import useFileDownloader from 'hooks/useFileDownloader';
import { getACGSignedUrl } from 'services/acg/utils';

const { TreeNode } = Tree;

type Props = {
  job: EmailExclusionJob,
};

export default function ExclusionJobUploadedFiles(props: Props) {
  const campaignsGroupedByFolder = _.groupBy(props.job.campaignFiles, 'folder');
  const foldersWithPriority = Object.entries(campaignsGroupedByFolder).map(
    ([folder, campaigns]) => {
      const priority = campaigns[0].priority;
      return { folder, priority, files: campaigns };
    },
  );
  return (
    <Tree defaultExpandAll showLine>
      <TreeNode title="Exclusion Files">
        {props.job.exclusionFiles.map(file => (
          <TreeNode
            key={`exclusion-${file.id}`}
            title={
              <FilenameRenderer
                failureReason={file.failureReason}
                filename={file.filename}
                gcsUrl={file.gcsUrl}
                status={file.status}
              />
            }
          />
        ))}
      </TreeNode>
      <TreeNode title="Campaign Files">
        {_.orderBy(foldersWithPriority, ['priority'], ['asc']).map(
          ({ folder, files }) => (
            <TreeNode key={`folder-${folder}`} title={folder}>
              {files.map(file => (
                <TreeNode
                  key={`campaign-${file.id}`}
                  title={
                    <FilenameRenderer
                      failureReason={file.failureReason}
                      filename={file.filename}
                      gcsUrl={file.gcsUrl}
                      processedGcsUrl={file.outputGcsUrl}
                      status={file.status}
                    />
                  }
                />
              ))}
            </TreeNode>
          ),
        )}
      </TreeNode>
    </Tree>
  );
}

function FilenameRenderer(props: {
  filename: string,
  gcsUrl: string,
  status: ExclusionFileStatus | CampaignFileStatus,
  processedGcsUrl?: string,
  failureReason?: string,
}) {
  const {
    filename,
    gcsUrl,
    status: fileStatus,
    processedGcsUrl,
    failureReason,
  } = props;
  const { loading, handleDownload } = useFileDownloader({
    file: { id: gcsUrl },
    getSignedUrl: id => getACGSignedUrl(id, 'phase1'),
    queryKey: 'acg-signed-url',
  });
  const {
    loading: processedLoading,
    handleDownload: handleProcessedDownload,
  } = useFileDownloader({
    file: { id: processedGcsUrl },
    getSignedUrl: id => getACGSignedUrl(id, 'phase1'),
    queryKey: 'acg-signed-url',
  });
  let statusColor;
  switch (fileStatus) {
    default:
    case 'pending':
      statusColor = 'orange';
      break;
    case 'importing':
    case 'processing':
      statusColor = 'blue';
      break;
    case 'imported':
      statusColor = 'cyan';
      break;
    case 'processed':
      statusColor = 'green';
      break;
    case 'failed':
      statusColor = 'red';
      break;
  }
  const renderTag = useMemo(() => {
    if (fileStatus === 'failed') {
      return (
        <FileErrorDetailsTag failureReason={failureReason || 'Unknown error'} />
      );
    }
    return (
      <Tag color={statusColor} style={{ marginRight: 0 }}>
        {fileStatus.toUpperCase()}
      </Tag>
    );
  }, [statusColor, fileStatus, failureReason]);
  return (
    <div className="flex space-x-1">
      {renderTag}
      {processedGcsUrl && (
        <div
          style={{ color: '#3366CC' }}
          onClick={e => {
            e.stopPropagation();
            if (!processedLoading) handleProcessedDownload();
          }}
        >
          Download Result
        </div>
      )}
      <div
        onClick={e => {
          e.stopPropagation();
          if (!loading) handleDownload();
        }}
      >
        {filename}
      </div>
    </div>
  );
}

function FileErrorDetailsTag(props: { failureReason: string }) {
  const { failureReason } = props;
  const [visible, setVisible] = React.useState(false);
  return (
    <>
      <Tooltip title="View Error Details">
        <Tag
          className="cursor-pointer"
          color="red"
          style={{ marginRight: 0 }}
          onClick={() => setVisible(true)}
        >
          FAILED
        </Tag>
      </Tooltip>
      <Modal
        title="Error Details"
        visible={visible}
        width={1000}
        onCancel={() => setVisible(false)}
        onOk={() => setVisible(false)}
      >
        <Alert message={failureReason} showIcon type="error" />
      </Modal>
    </>
  );
}
